import React, { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../App'
import { Eye, EyeOff, ArrowRight } from 'lucide-react'
import { MeshGradient } from '@paper-design/shaders-react'

export default function Login() {
  const { user, login } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [transitioning, setTransitioning] = useState(false)

  if (user && !transitioning) {
    return <Navigate to="/dashboard" replace />
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login(email, password)
      setTransitioning(true)
      setTimeout(() => {
        navigate('/dashboard')
      }, 800)
    } catch (err) {
      setError(err.message)
      setLoading(false)
    }
  }

  return (
    <div className="page" style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: 'var(--space-lg)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Mesh Gradient Background */}
      <MeshGradient
        style={{
          position: 'absolute',
          inset: '-20px',
          width: 'calc(100% + 40px)',
          height: 'calc(100% + 40px)',
          zIndex: 0,
          filter: 'blur(3px)'
        }}
        colors={['#000000', '#1a1a1a', '#333333', '#ffffff']}
        speed={transitioning ? 1.5 : 0.75}
        backgroundColor="#000000"
      />
      
      {/* Noise overlay */}
      <div style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        opacity: 0.15,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='5' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        pointerEvents: 'none'
      }} />

      {/* Transition overlay */}
      <AnimatePresence>
        {transitioning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: 'easeInOut' }}
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              background: 'var(--bg-primary)',
            }}
          />
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ 
          opacity: transitioning ? 0 : 1, 
          y: transitioning ? -30 : 0,
          scale: transitioning ? 0.95 : 1
        }}
        transition={{ duration: 0.5 }}
        style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}
      >
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }}>
          <h1 style={{ marginBottom: 'var(--space-sm)' }}>NuMuN Next</h1>
          <p className="text-secondary">Guidance that grows with your life</p>
        </div>

        <div className="glass-card-static" style={{ padding: 'var(--space-xl)' }}>
          <form onSubmit={handleSubmit}>
            <div className="input-group" style={{ marginBottom: 'var(--space-lg)' }}>
              <label className="input-label">Email</label>
              <input
                type="email" className="input" placeholder="you@example.com"
                value={email} onChange={(e) => setEmail(e.target.value)}
                required autoFocus
              />
            </div>

            <div className="input-group" style={{ marginBottom: 'var(--space-lg)' }}>
              <label className="input-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="input" placeholder="••••••••"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  required style={{ paddingRight: '48px' }}
                />
                <button
                  type="button" onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute', right: 12, top: '50%',
                    transform: 'translateY(-50%)', background: 'none',
                    border: 'none', cursor: 'pointer',
                    color: 'var(--text-tertiary)', padding: 4
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  padding: 'var(--space-md)', marginBottom: 'var(--space-lg)',
                  background: 'var(--accent-red-muted)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--accent-red)', fontSize: '0.875rem'
                }}
              >
                {error}
              </motion.div>
            )}

            <button 
              type="submit" className="btn btn-primary btn-lg"
              disabled={loading || transitioning}
              style={{ width: '100%', opacity: loading ? 0.7 : 1 }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
              {!loading && <ArrowRight size={18} />}
            </button>
          </form>
        </div>

        <p style={{ 
          textAlign: 'center', marginTop: 'var(--space-xl)',
          fontSize: '0.875rem', color: 'var(--text-tertiary)'
        }}>
          Learn. Grow. Prove. Advance.
        </p>
      </motion.div>
    </div>
  )
}
